import type {
  SoundClip,
  SoundClipApi,
  SoundClipListParams,
} from './soundClipApi.types'

export const MOCK_SOUND_CLIPS: SoundClip[] = [
  {
    id: 1,
    name: 'Main Greeting',
    feature_code: 100,
    location:
      'https://s3.us-west-2.amazonaws.com/sound-clips/main_greeting.wav',
    file_url:
      'https://s3.us-west-2.amazonaws.com/sound-clips/main_greeting.wav?presigned=1',
    pbx_id: 1,
    pbx_uuid: 'pbx-uuid-001',
    created_at: '2025-06-15T08:00:00Z',
    updated_at: '2025-12-10T14:30:00Z',
  },
  {
    id: 2,
    name: 'After Hours Message',
    feature_code: 101,
    location: 'https://s3.us-west-2.amazonaws.com/sound-clips/after_hours.wav',
    file_url:
      'https://s3.us-west-2.amazonaws.com/sound-clips/after_hours.wav?presigned=1',
    pbx_id: 1,
    pbx_uuid: 'pbx-uuid-001',
    created_at: '2025-07-20T10:00:00Z',
    updated_at: '2025-11-01T09:15:00Z',
  },
  {
    id: 3,
    name: 'Holiday Announcement',
    feature_code: 102,
    location: 'https://s3.us-west-2.amazonaws.com/sound-clips/holiday.wav',
    file_url:
      'https://s3.us-west-2.amazonaws.com/sound-clips/holiday.wav?presigned=1',
    pbx_id: 1,
    pbx_uuid: 'pbx-uuid-001',
    created_at: '2025-12-01T12:00:00Z',
    updated_at: '2025-12-01T12:00:00Z',
  },
  {
    id: 4,
    name: 'Transfer Prompt',
    feature_code: 0,
    location: 'https://s3.us-west-2.amazonaws.com/sound-clips/transfer.wav',
    file_url:
      'https://s3.us-west-2.amazonaws.com/sound-clips/transfer.wav?presigned=1',
    pbx_id: 1,
    pbx_uuid: 'pbx-uuid-001',
    created_at: '2025-08-05T16:00:00Z',
    updated_at: '2025-10-18T11:45:00Z',
  },
  {
    id: 5,
    name: 'Queue Wait Message',
    feature_code: 103,
    location: 'https://s3.us-west-2.amazonaws.com/sound-clips/queue_wait.wav',
    file_url:
      'https://s3.us-west-2.amazonaws.com/sound-clips/queue_wait.wav?presigned=1',
    pbx_id: 1,
    pbx_uuid: 'pbx-uuid-001',
    created_at: '2025-09-15T09:00:00Z',
    updated_at: '2026-01-05T09:00:00Z',
  },
]

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const createMockSoundClipApi = (): SoundClipApi => {
  let soundClips = [...MOCK_SOUND_CLIPS]

  return {
    async getSoundClips(params: SoundClipListParams) {
      await delay(400)
      let filtered = [...soundClips]

      if (params.search) {
        const lower = params.search.toLowerCase()
        filtered = filtered.filter(
          (sc) =>
            sc.name.toLowerCase().includes(lower) ||
            `*${sc.feature_code}`.includes(lower),
        )
      }

      if (params.ordering) {
        const desc = params.ordering.startsWith('-')
        const field = params.ordering.replace(/^-/, '') as keyof SoundClip
        filtered.sort((a, b) => {
          const cmp = String(a[field] ?? '').localeCompare(
            String(b[field] ?? ''),
            undefined,
            { numeric: true },
          )
          return desc ? -cmp : cmp
        })
      }

      const totalCount = filtered.length
      const offset = params.offset ?? 0
      const limit = params.limit ?? 25
      const results = filtered.slice(offset, offset + limit)

      return { results, totalCount }
    },

    async getSoundClip(id: number) {
      await delay(300)
      const clip = soundClips.find((sc) => sc.id === id)
      if (!clip) throw new Error(`Sound clip ${id} not found`)
      return { ...clip }
    },

    async createSoundClip(payload) {
      await delay(600)
      const newClip: SoundClip = {
        id: Math.max(...soundClips.map((sc) => sc.id)) + 1,
        name: payload.name,
        feature_code: payload.feature_code,
        location: `https://s3.us-west-2.amazonaws.com/sound-clips/${payload.audio_file.name}`,
        file_url: `https://s3.us-west-2.amazonaws.com/sound-clips/${payload.audio_file.name}?presigned=1`,
        pbx_id: payload.pbx_id,
        pbx_uuid: 'pbx-uuid-001',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      soundClips = [newClip, ...soundClips]
      return newClip
    },

    async updateSoundClip(id, payload) {
      await delay(400)
      const idx = soundClips.findIndex((sc) => sc.id === id)
      if (idx === -1) throw new Error(`Sound clip ${id} not found`)

      const updated: SoundClip = {
        ...soundClips[idx],
        ...(payload.name !== undefined && { name: payload.name }),
        ...(payload.feature_code !== undefined && {
          feature_code: payload.feature_code,
        }),
        ...(payload.audio_file && {
          location: `https://s3.us-west-2.amazonaws.com/sound-clips/${payload.audio_file.name}`,
          file_url: `https://s3.us-west-2.amazonaws.com/sound-clips/${payload.audio_file.name}?presigned=1`,
        }),
        updated_at: new Date().toISOString(),
      }
      soundClips[idx] = updated
      return updated
    },

    async deleteSoundClip(id) {
      await delay(300)
      soundClips = soundClips.filter((sc) => sc.id !== id)
    },
  }
}

/** @deprecated Use createMockSoundClipApi() for stateful mocks */
export const mockSoundClipApi = createMockSoundClipApi()
